var _a, _b, _c, _d, _e, _f;
import { __decorate, __metadata } from "tslib";
import { Component } from 'decorators';
import view from './instructions.view.html';
import { translate } from 'angular';
import { selectableRoles, role } from '../../../types/instructions';
import './instructions.scss';
import { AgentSegmentService } from '../../../services/AgentSegmentService';
import { BUILDER_AGENT_CHATHISTORY_ADDED } from '../../../events';
import { BuilderFeatures } from '../../../../BuilderFeatures';
import { AlertService } from 'modules/shared/AlertService';
import SparkleGradientIcon from 'assets/img/icons/sparkle-ai-gradient.svg';
import SparkleLoadingAnimation from 'assets/img/sparkle-processing.svg';
import OptimizedPromptModal from './components/instructionConfiguration/OptimizedPromptModal';
import { ROLE_ICON_MAP } from '../../../utils/promptUtils';
import { BlipToastService } from 'modules/shared/BlipToastService';
import { copyInstructionById, createEmptyInstruction, generatePromptId, instructionsEqual, removeInstructionById, toAssistantPrompts, } from './promptState';
import { AgentPromptService } from '../../../services/AgentPromptService';
import { InstructionJobOrchestratorService } from '../../../services/InstructionJobOrchestratorService';
export var AiInstructionOrigin;
(function (AiInstructionOrigin) {
    AiInstructionOrigin["None"] = "none";
    AiInstructionOrigin["Generate"] = "generate";
    AiInstructionOrigin["Optimize"] = "optimize";
})(AiInstructionOrigin || (AiInstructionOrigin = {}));
let Instructions = class Instructions {
    static $inject = ['AgentSegmentService', 'AlertService', '$translate', 'ModalService', 'AgentPromptService', 'InstructionJobOrchestratorService'];
    //#endregion
    constructor(AgentSegmentService, AlertService, $translate, ModalService, // ModalService type not defined on the project
    BlipToastService, AgentPromptService, InstructionJobOrchestratorService) {
        this.AgentSegmentService = AgentSegmentService;
        this.AlertService = AlertService;
        this.$translate = $translate;
        this.ModalService = ModalService;
        this.BlipToastService = BlipToastService;
        this.AgentPromptService = AgentPromptService;
        this.InstructionJobOrchestratorService = InstructionJobOrchestratorService;
        //#region Consts
        this.promptRoles = selectableRoles;
        this.roleIconMap = ROLE_ICON_MAP;
        this.sparkleGradientIcon = SparkleGradientIcon;
        this.sparkleLoadingAnimation = SparkleLoadingAnimation;
        //#endregion
        //#region Properties
        this.selectedModel = '';
        this.instructions = [createEmptyInstruction(role.system, generatePromptId)];
        this.prompts = this.instructions;
        this.isPromptOptimizationEnabled = false;
        this.isGenerateInstructionFormOpen = false;
        this.currentGeneratingStatusText = '';
        this.bannerText = '';
        this.promptContext = '';
        this.optimizedPrompts = [];
        this.cancelledGenerateInstructions = false;
        this.instructionErrorHeader = '';
        this.instructionErrorDescription = '';
        this.onRetry = null;
        this.viewState = 'list';
        this.isDestroyed = false; // Guard against race conditions when component is destroyed
        this.aiInstructionOrigin = AiInstructionOrigin.None;
        //#region Lifecycle Hooks
        this.$onInit = () => {
            this.ngSortableOptions = {
                animation: 1,
                scroll: true,
                handle: '.anchor',
                disabled: false,
                onSort: (e) => {
                    this.onSortChange(e.models);
                },
            };
            this.initializeAgentConfiguration();
            this.initializeFeatures();
            this.InstructionJobOrchestratorService.init();
        };
        this.$onChanges = (changes) => {
            if (changes.agentConfiguration && changes.agentConfiguration.currentValue) {
                this.initializeAgentConfiguration();
            }
        };
        this.$doCheck = () => {
            const currentModel = this.agentConfiguration?.model?.model || '';
            if (currentModel !== this.selectedModel) {
                this.selectedModel = currentModel;
            }
            const currentPrompts = this.agentConfiguration?.prompt || [];
            if (!instructionsEqual(currentPrompts, this.prompts)) {
                this.prompts = currentPrompts;
                this.instructions = toAssistantPrompts(currentPrompts, generatePromptId);
            }
        };
        this.$onDestroy = () => {
            this.isDestroyed = true;
            this.InstructionJobOrchestratorService.destroy();
        };
    }
    // #endregion
    //#region Public Methods
    async initializeFeatures() {
        this.isPromptOptimizationEnabled = await BuilderFeatures.isStudioPromptOptimizationEnabled();
    }
    initializeAgentConfiguration() {
        if (!this.agentConfiguration) {
            return;
        }
        this.selectedModel = this.agentConfiguration?.model?.model || '';
        this.prompts = this.agentConfiguration?.prompt || [];
        this.instructions = toAssistantPrompts(this.agentConfiguration?.prompt, generatePromptId);
    }
    openModelAdjustmentModal() {
        this.onOpenModelAdjustmentModal?.();
    }
    createInstruction(currentRole) {
        const newInstruction = createEmptyInstruction(currentRole, generatePromptId);
        // Find the index of short-term memory instruction
        const shortTermMemoryIndex = this.instructions.findIndex((i) => i.type === role.shortTermMemory);
        // If short-term memory exists, insert before it; otherwise, push to the end
        if (shortTermMemoryIndex !== -1) {
            this.instructions.splice(shortTermMemoryIndex, 0, newInstruction);
        }
        else {
            this.instructions.push(newInstruction);
        }
        this.syncAgentConfiguration();
        this.AgentSegmentService.trackAiAgentBlock(BUILDER_AGENT_CHATHISTORY_ADDED);
    }
    onGenerateInstructions() {
        const modalTitle = this.$translate.instant('instructions.alert.replaceInstructions.title');
        const modalBody = this.$translate.instant('instructions.alert.replaceInstructions.text');
        const confirm = this.$translate.instant('instructions.alert.replaceInstructions.confirm');
        const cancel = this.$translate.instant('instructions.alert.replaceInstructions.cancel');
        this.AlertService.show({
            title: modalTitle,
            body: modalBody,
            variant: 'warning',
            icon: 'warning',
            buttons: {
                cancel: cancel,
                confirm: confirm,
            },
            callback: {
                cancelButtonAction: () => { },
                confirmButtonAction: async () => {
                    this.viewState = 'form';
                },
            },
        });
    }
    closeGenerateInstructionForm() {
        this.viewState = 'list';
    }
    cancelGenerateInstructions() {
        this.InstructionJobOrchestratorService.cancel();
        this.cancelledGenerateInstructions = true;
        this.viewState = 'list';
        this.currentGeneratingStatusText = '';
        this.optimizedPrompts = [];
        this.aiInstructionOrigin = AiInstructionOrigin.None;
        this.promptContext = '';
    }
    async generateInstructions() {
        this.aiInstructionOrigin = AiInstructionOrigin.Generate;
        this.runInstructionJob(() => this.AgentPromptService.generateInstructions(this.promptContext, this.stateId), (prompt) => {
            const shortTermMemory = this.prompts.find(p => p.type === role.shortTermMemory);
            this.optimizedPrompts = shortTermMemory ? [...prompt, shortTermMemory] : prompt;
        }, {
            header: this.$translate.instant('instructions.errors.generating.header'),
            description: this.$translate.instant('instructions.errors.generating.description'),
            onRetry: this.generateInstructions.bind(this),
        });
    }
    onPromptContextChange(event) {
        this.promptContext = event.detail.value;
    }
    ;
    deleteInstruction(instructionId) {
        this.instructions = removeInstructionById(this.instructions, instructionId);
        this.syncAgentConfiguration();
    }
    copyInstruction(instructionId) {
        this.instructions = copyInstructionById(this.instructions, instructionId, generatePromptId);
        this.syncAgentConfiguration();
    }
    saveInstruction(instruction) {
        const index = this.instructions.findIndex((i) => i.promptId === instruction.promptId);
        if (index !== -1) {
            this.instructions = [...this.instructions.slice(0, index), instruction, ...this.instructions.slice(index + 1)];
            this.syncAgentConfiguration();
        }
    }
    onSortChange(instructions) {
        this.instructions = instructions;
        this.syncAgentConfiguration();
    }
    async openOptimizedInstructionsModal() {
        await this.ModalService.showModal(OptimizedPromptModal({
            optimizedInstructions: this.optimizedPrompts,
            originalInstructions: this.prompts
        }));
    }
    closeOptimizedPromptResult() {
        this.AlertService.show({
            title: this.$translate.instant(`instructions.alert.cancel.${this.aiInstructionOrigin}.title`),
            body: this.$translate.instant(`instructions.alert.cancel.${this.aiInstructionOrigin}.text`),
            variant: 'warning',
            icon: 'warning',
            buttons: {
                cancel: this.$translate.instant('instructions.alert.cancel.cancel'),
                confirm: this.$translate.instant('instructions.alert.cancel.confirm'),
            },
            callback: {
                cancelButtonAction: () => { },
                confirmButtonAction: () => this.cancelGenerateInstructions()
            }
        });
    }
    applyOptimizedPrompts() {
        this.instructions = toAssistantPrompts(this.optimizedPrompts, generatePromptId);
        this.syncAgentConfiguration();
        this.BlipToastService.show('success', {
            msg: this.$translate.instant('instructions.toast.success.appliedInstructions.text'),
            title: this.$translate.instant('instructions.toast.success.appliedInstructions.title'),
        });
        this.cancelGenerateInstructions();
        this.viewState = 'list';
    }
    async onOptimizeInstructions() {
        this.aiInstructionOrigin = AiInstructionOrigin.Optimize;
        this.runInstructionJob(() => this.AgentPromptService.optimizeInstructions(this.prompts, this.stateId), (prompt) => {
            this.optimizedPrompts = prompt;
        }, {
            header: this.$translate.instant('instructions.errors.optimizing.header'),
            description: this.$translate.instant('instructions.errors.optimizing.description'),
            onRetry: this.onOptimizeInstructions.bind(this),
        });
    }
    async retryInstruction() {
        this.viewState = 'generating';
        await this.onRetry?.();
    }
    // #endregion
    // #region Helpers
    runInstructionJob(job, onSuccess, onJobFailure) {
        this.viewState = 'generating';
        this.bannerText = this.$translate.instant(`instructions.banner.alert.${this.aiInstructionOrigin}`);
        this.InstructionJobOrchestratorService.runJob(job, onSuccess, (status) => {
            // Update UI with status text
            this.currentGeneratingStatusText = status.statusText;
        }, () => {
            // Handle error
            this.handleInstructionJobError(onJobFailure);
        }, () => {
            // On completion, transition to optimized view
            this.viewState = 'optimized';
        }, 2000 // polling interval
        );
    }
    handleInstructionJobError(onJobFailure) {
        this.InstructionJobOrchestratorService.cancel();
        this.viewState = 'error';
        this.currentGeneratingStatusText = '';
        this.BlipToastService.show('danger', {
            title: this.$translate.instant('instructions.toast.failure.generalErrorInstruction.title'),
            msg: this.$translate.instant('instructions.toast.failure.generalErrorInstruction.text'),
        });
        this.instructionErrorHeader = onJobFailure.header;
        this.instructionErrorDescription = onJobFailure.description;
        this.onRetry = onJobFailure.onRetry;
    }
    syncAgentConfiguration() {
        if (this.saveAgentInstructions) {
            this.agentConfiguration.prompt = this.instructions;
            this.saveAgentInstructions();
        }
    }
};
//#endregion
//#region Dependency Injection and Constructor
Instructions.$inject = [
    'AgentSegmentService',
    'AlertService',
    '$translate',
    'ModalService',
    'BlipToastService',
    'AgentPromptService',
    'InstructionJobOrchestratorService'
];
Instructions = __decorate([
    Component({
        controllerAs: '$ctrl',
        template: view,
        bindings: {
            onOpenModelAdjustmentModal: '&?',
            onOpenGuardrailModal: '&?',
            saveAgentInstructions: '&?',
            agentConfiguration: '<',
            stateId: '<?', // State ID of the current agent being edited
        },
    }),
    __metadata("design:paramtypes", [typeof (_a = typeof AgentSegmentService !== "undefined" && AgentSegmentService) === "function" ? _a : Object, typeof (_b = typeof AlertService !== "undefined" && AlertService) === "function" ? _b : Object, typeof (_c = typeof translate !== "undefined" && translate.ITranslateService) === "function" ? _c : Object, Object, typeof (_d = typeof BlipToastService !== "undefined" && BlipToastService) === "function" ? _d : Object, typeof (_e = typeof AgentPromptService !== "undefined" && AgentPromptService) === "function" ? _e : Object, typeof (_f = typeof InstructionJobOrchestratorService !== "undefined" && InstructionJobOrchestratorService) === "function" ? _f : Object])
], Instructions);
export { Instructions };
